import React from 'react';
import { Card, Typography } from 'antd';
import { PictureOutlined } from '@ant-design/icons';
import { baseColor200, baseColor400 } from '@seaters/ui';
import { useTranslation } from 'react-i18next';
import { FormTranslationType } from '@seaters/constants';
import { getMapTranslation } from '@seaters/data-access';
import { Link } from 'react-router-dom';
import cx from 'classnames';

const { Paragraph } = Typography;

interface MobileCardLayoutProps {
  imageSrc?: string;
  imageAlt?: string;
  showImagePlaceholder?: boolean;
  placeholderIcon?: React.ReactNode;
  imageOverlay?: React.ReactNode;
  title?: string;
  titleComponent?: React.ReactNode;
  children?: React.ReactNode;
  priceSection?: React.ReactNode;
  to?: string;
  redirectFrom?: string;
  className?: string;
  eventName?: FormTranslationType;
  lang: string;
  waitingListId?: string;
}

export function MobileCardLayout({
  imageSrc,
  imageAlt = 'Card image',
  showImagePlaceholder = false,
  placeholderIcon,
  imageOverlay,
  title,
  titleComponent,
  children,
  priceSection,
  to,
  redirectFrom,
  className,
  eventName,
  lang,
  waitingListId,
}: MobileCardLayoutProps) {
  const { t } = useTranslation();
  const defaultPlaceholderIcon = (
    <PictureOutlined style={{ fontSize: '28px', color: baseColor400 }} />
  );

  const cardContent = (
    <Card
      variant="borderless"
      className={cx(
        '!rounded-2xl !shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)]',
        className
      )}
      classNames={{ body: '!p-0' }}
      aria-label={
        t('waiting_list.view_details', {
          defaultValue: 'View details for {{eventName}}',
          eventName: getMapTranslation(eventName, lang),
        }) ?? undefined
      }
      {...(waitingListId && { 'data-waiting-list-id': waitingListId })}
    >
      <div className="flex flex-col w-full">
        {/* Image at the top */}
        <div className="w-full rounded-2xl overflow-hidden relative">
          {imageSrc ? (
            <img
              className="w-full h-auto object-cover"
              alt={imageAlt}
              src={imageSrc}
            />
          ) : showImagePlaceholder ? (
            <div
              className="w-full h-[150px] flex items-center justify-center"
              style={{ backgroundColor: baseColor200 }}
            >
              {placeholderIcon || defaultPlaceholderIcon}
            </div>
          ) : null}
          {imageOverlay}
        </div>

        {/* Content section */}
        <div className="flex flex-col w-full">
          {/* Title */}
          {(title || titleComponent) && (
            <div className="px-3 pt-3 pb-2 w-full">
              {titleComponent || (
                <Paragraph
                  strong
                  className="!m-0 !p-0 !text-base !font-bold !leading-[1.5]"
                  ellipsis={{ rows: 2, expandable: false, tooltip: title }}
                >
                  {title}
                </Paragraph>
              )}
            </div>
          )}

          {/* Custom content */}
          {children && (
            <div className="flex flex-col pb-2 w-full">{children}</div>
          )}

          {/* Price section with border */}
          {priceSection}
        </div>
      </div>
    </Card>
  );

  if (to) {
    return (
      <Link to={to} state={{ from: redirectFrom }} className="block">
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}

export default MobileCardLayout;
